import React, { useState, useEffect, useMemo } from 'react';
import { GetIBTreeApi } from "../../../api/request/client.js";
import { useSearchParams } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { Table, Input } from 'antd';
const colors = [
    '#539198',
    '#c04e4e',
    '#c79f34',
    '#656d37',
    '#a1bdbe',
    '#ccc5c5',
    '#af3429',
];
const getColor = (level) => {
    const colorIndex = (level - 1) % colors.length;
    let color
    if (level == 0) {
        color = '#da856c'
    } else {
        color = colors[colorIndex];
    }
    return color;
}
const formatMoney = (value) =>
    `$ ${Number(value || 0).toLocaleString("en-US", {
        maximumFractionDigits: 2,
    })}`;

const toLoginIds = (loginId) => {
    if (Array.isArray(loginId)) {
        return loginId.filter((id) => id !== null && id !== undefined && id !== "");
    }
    if (loginId === null || loginId === undefined || loginId === "") return [];
    return [loginId];
};

const renderLoginIds = (loginIds) => {
    if (!loginIds?.length) return <span>--</span>;
    const rows = [];
    for (let i = 0; i < loginIds.length; i += 2) {
        rows.push(loginIds.slice(i, i + 2).join(", "));
    }
    return (
        <div className="flex flex-col gap-1 whitespace-nowrap min-w-[200px]">
            {rows.map((row) => (
                <span key={row}>{row}</span>
            ))}
        </div>
    );
};

const columns = (openUserDetails) => [
    {
        title: 'Name',
        dataIndex: 'name',
        render: (_, record) => (
            <p
                className='f-bold'
                style={{ color: getColor(record?.level), fontWeight: 'bold', fontSize: 16, cursor: 'pointer' }}
                onClick={() => openUserDetails(record?.key)}
            >
                {record?.name}
            </p>
        ),
    },
    {
        title: 'Level',
        dataIndex: 'level',
    },
    {
        title: 'Count',
        dataIndex: 'count',
    },
    {
        title: 'Email',
        dataIndex: 'email',
    },
    {
        title: 'Mobile No',
        dataIndex: 'mobileNo'
    },
    {
        title: 'Login ID',
        dataIndex: 'loginIds',
        width: 220,
        render: renderLoginIds,
    },
    {
        title: 'Total Deposit',
        dataIndex: 'totalDeposit',
        render: (value) => formatMoney(value),
    },
    {
        title: 'Total Withdrawal',
        dataIndex: 'totalWithdrawal',
        render: (value) => formatMoney(value),
    },
    {
        title: 'Net Deposit',
        dataIndex: 'netDeposit',
        render: (_, record) =>
            formatMoney(
                Number(record?.totalDeposit || 0) -
                    Number(record?.totalWithdrawal || 0)
            ),
    },
    {
        title: 'IB Commission / Rebate',
        dataIndex: 'ibCommissionRebate',
        render: (value) =>
            value === 0 || value ? formatMoney(value) : '--',
    },
];

const nodeMatchesSearch = (node, needle) =>
    node?.name?.toLowerCase().includes(needle) ||
    node?.email?.toLowerCase().includes(needle) ||
    node?.mobileNo?.toLowerCase().includes(needle) ||
    node?.loginIds?.some((id) =>
        String(id ?? "").toLowerCase().includes(needle)
    );

const filterTree = (nodes, term) => {
    const needle = term.trim().toLowerCase();
    if (!needle) return nodes;

    const walk = (node) => {
        if (nodeMatchesSearch(node, needle)) return { ...node };

        const matchedChildren = (node?.children || []).map(walk).filter(Boolean);
        if (!matchedChildren.length) return null;
        return { ...node, children: matchedChildren };
    };

    return (nodes || []).map(walk).filter(Boolean);
};

const collectKeys = (nodes, keys = []) => {
    (nodes || []).forEach((n) => {
        keys.push(n.key);
        if (n?.children?.length) collectKeys(n.children, keys);
    });
    return keys;
};

function IBTree() {

    const [isLoading, setIsLoading] = useState(false);
    const [treeData, setTreeData] = useState();
    const [searchParams] = useSearchParams();
    const [data, setData] = useState();
    const [searchText, setSearchText] = useState("");
    const { id } = useParams();

    const filteredTree = useMemo(
        () => filterTree(treeData, searchText),
        [treeData, searchText]
    );

    const expandedRowKeys = useMemo(
        () => (searchText.trim() ? collectKeys(filteredTree) : undefined),
        [filteredTree, searchText]
    );

    const openUserDetails = (selectedUser) => {
        const win = window.open(`/client_details/${selectedUser}`, "_blank");
        win.focus();
    }

    const convertDataToTree = (data) => {
        const traverse = (node) => {
            const children = Array.isArray(node?.childrenUser)
                ? node.childrenUser
                : [];
            return {
                name: node?.fullName,
                level: node?.level || 0,
                key: `${node?.userId}`,
                count: node?.userCount || 0,
                email: node?.email,
                mobileNo: node?.phoneNo ? node?.phoneNo : '--',
                loginIds: toLoginIds(node?.loginId),
                totalDeposit: node?.totalDeposit,
                totalWithdrawal: node?.totalWithdrawal,
                ibCommissionRebate: node?.ibCommissionRebate,
                children: children.length ? children.map(traverse) : null,
            };
        };
        return [traverse(data)];
    };

    const getAllTrader = async () => {
        try {
            setIsLoading(true);

            const { data } = await GetIBTreeApi(id);
            if (data?.success) {
                setData(JSON.parse(data?.data));
                setTreeData(convertDataToTree(JSON.parse(data?.data)));
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getAllTrader();
    }, []);

    return (
        <div>
        <div className='react-tree-wrapper'>
            <div className='react-flow-container p-4'>
                {isLoading ? (
                    <div className='text-center text-muted'> Loading...</div>
                ) : (
                    <div className='m-3'>
                        <div className="flex flex-wrap items-center justify-end gap-4 mb-4">
                            <Input
                                allowClear
                                size="large"
                                style={{ width: 280 }}
                                placeholder="Search"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                prefix={
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="w-5 h-5"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                }
                            />
                        </div>
                        <Table
                            columns={columns(openUserDetails)}
                            dataSource={filteredTree}
                            pagination={false}
                            scroll={{ x: "max-content" }}
                            expandable={
                                expandedRowKeys
                                    ? { expandedRowKeys, onExpand: () => {} }
                                    : undefined
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    </div>
    )
}

export default IBTree;
